import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number; // Timestamp when token expires
}


interface AuthState {
  tokens: AuthTokens | null;
  isLoading: boolean;
  isInitialized: boolean; // Track if Firebase has completed initial auth check

  // Actions
  setTokens: (tokens: AuthTokens) => void;
  clearAuth: () => void;
  setInitialized: (initialized: boolean) => void;
}

// Create auth store with persistence
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      tokens: null,
      isLoading: false,
      isInitialized: false,

      setTokens: (tokens: AuthTokens) =>
        set(() => ({
          tokens,
          // When tokens are set (non-null), we are starting auth initialization
          // and will mark initialized=true once Firebase confirms the user.
          isInitialized: false,
        })),

      clearAuth: () =>
        set(() => ({
          tokens: null,
          // If there are no tokens, initialization is complete (user is signed out)
          isInitialized: true,
        })),

      setInitialized: (initialized: boolean) =>
        set(() => ({
          isInitialized: initialized,
        })),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        tokens: state.tokens,
      }),
    }
  )
);

// Helper function to check if token is expired or about to expire
export const isTokenExpired = (expiresAt: number, bufferSeconds = 300): boolean => {
  // Buffer of 5 minutes (300 seconds) by default
  return Date.now() >= (expiresAt - bufferSeconds * 1000);
};
